/**
 * Version-based cache invalidation utility
 *
 * This utility detects when a new version of the app has been deployed and
 * automatically clears stale caches to prevent issues like:
 * - Old tokens being used with new API endpoints
 * - Stale session data causing authentication failures
 * - Users needing to manually clear cookies after deployments
 * - "JS/CSS not found" errors due to stale chunk references
 */

const VERSION_KEY = "app.version";
const LAST_CLEANUP_KEY = "app.last.cleanup";

// Use build timestamp as version - this changes with every deployment
const APP_VERSION = import.meta.env.VITE_BUILD_TIMESTAMP || "dev";

/**
 * Keys that should be cleared on version change
 * These are app-specific caches that may become stale after deployment
 */
const CLEARABLE_KEYS = [
  "skypoint-studio-root", // Redux persist key
  "app.token.cache",
  "app.cold.start.done",
  "app.user.checked",
];

/**
 * MSAL keys that should be preserved during cleanup
 * These are needed for in-progress authentication flows
 */
const MSAL_PRESERVE_PATTERNS = [".interaction.status", ".request.state", ".temp.state"];

/**
 * Check if a key should be preserved (not cleared)
 */
function shouldPreserveKey(key: string): boolean {
  return MSAL_PRESERVE_PATTERNS.some((pattern) => key.includes(pattern));
}

/**
 * Clear all app-specific caches from localStorage
 */
function clearAppCaches(): void {
  // Clear known app cache keys
  CLEARABLE_KEYS.forEach((key) => {
    localStorage.removeItem(key);
  });

  // Clear any MSAL cache keys (except interaction state)
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith("msal.") && !shouldPreserveKey(key)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));
}

/**
 * Clear session storage (but preserve interaction state if present)
 */
function clearSessionCaches(): void {
  // Get keys to preserve
  const keysToPreserve: { key: string; value: string }[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && shouldPreserveKey(key)) {
      const value = sessionStorage.getItem(key);
      if (value) {
        keysToPreserve.push({ key, value });
      }
    }
  }

  // Clear all session storage
  sessionStorage.clear();

  // Restore preserved keys
  keysToPreserve.forEach(({ key, value }) => {
    sessionStorage.setItem(key, value);
  });
}

/**
 * Clear cookies related to the app session
 * Note: Only clears cookies that can be accessed via JavaScript
 */
function clearAppCookies(): void {
  // List of cookie names to clear
  const cookiesToClear = ["msal.session.start", "app.user.checked"];

  cookiesToClear.forEach((name) => {
    // Clear cookie by setting expiry to past
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  });
}

/**
 * Check if we're returning from an OAuth callback.
 * In this case, we should NOT clear caches as MSAL needs to process the response.
 */
function isOAuthCallback(): boolean {
  const path = window.location.pathname;
  const hash = window.location.hash;
  const search = window.location.search;

  return (
    path.includes("/auth/callback") ||
    hash.includes("code=") ||
    hash.includes("id_token=") ||
    hash.includes("access_token=") ||
    search.includes("code=")
  );
}

/**
 * Check if app version has changed and clear caches if needed.
 *
 * This should be called early in the app initialization, BEFORE
 * authentication is processed, to ensure stale tokens don't cause issues.
 *
 * IMPORTANT: If we're returning from an OAuth callback, we skip the cleanup
 * to allow MSAL to process the authentication response first.
 *
 * @returns Object with:
 *   - versionChanged: true if version changed and caches were cleared
 *   - previousVersion: the previous app version (if any)
 *   - currentVersion: the current app version
 */
export function checkAndClearOnVersionChange(): {
  versionChanged: boolean;
  previousVersion: string | null;
  currentVersion: string;
} {
  const storedVersion = localStorage.getItem(VERSION_KEY);
  const result = {
    versionChanged: false,
    previousVersion: storedVersion,
    currentVersion: APP_VERSION,
  };

  // If version is different (or first time), clear caches
  if (storedVersion !== APP_VERSION) {
    // CRITICAL: Don't clear caches if we're returning from OAuth callback
    // MSAL needs to process the redirect response before any cleanup
    if (isOAuthCallback()) {
      console.log(
        `[VersionCheck] Version changed but OAuth callback detected. Deferring cleanup until after auth completes.`,
      );
      // Store version but don't clear caches yet - they'll be cleared on next non-callback load
      localStorage.setItem(VERSION_KEY, APP_VERSION);
      return result;
    }

    console.log(
      `[VersionCheck] App version changed: ${storedVersion || "none"} → ${APP_VERSION}. Clearing caches...`,
    );

    // Clear all caches
    clearAppCaches();
    clearSessionCaches();
    clearAppCookies();

    // Store new version
    localStorage.setItem(VERSION_KEY, APP_VERSION);
    localStorage.setItem(LAST_CLEANUP_KEY, new Date().toISOString());

    result.versionChanged = true;

    console.log("[VersionCheck] Cache cleanup complete. Fresh state for new version.");
  }

  return result;
}

/**
 * Force clear all caches regardless of version.
 * Useful for manual cache clearing or debugging.
 */
export function forceClearAllCaches(): void {
  console.log("[VersionCheck] Force clearing all caches...");

  clearAppCaches();
  clearSessionCaches();
  clearAppCookies();

  // Update cleanup timestamp but keep version
  localStorage.setItem(LAST_CLEANUP_KEY, new Date().toISOString());

  console.log("[VersionCheck] Force cache cleanup complete.");
}

/**
 * Get the current app version
 */
export function getAppVersion(): string {
  return APP_VERSION;
}

/**
 * Get the last cleanup timestamp
 */
export function getLastCleanupTime(): string | null {
  return localStorage.getItem(LAST_CLEANUP_KEY);
}

// ── New-deploy detection (stale-SPA-bundle guard) ──────────────────────────
// An SPA never re-fetches index.html as the user navigates, so a long-lived tab
// keeps running the JS bundle it first loaded. After a deploy it can silently
// run stale code (e.g. an outdated header logo that won't react to a mode
// toggle) until a full reload. checkAndClearOnVersionChange() above only runs at
// page load; this detects a newer build WHILE the tab is open and lets the UI
// offer a refresh.

/** The hashed entry bundle this tab is RUNNING (null in dev — entry is /src/…). */
function runningEntryPath(): string | null {
  const scripts = Array.from(
    document.querySelectorAll<HTMLScriptElement>('script[type="module"][src]'),
  );
  for (const s of scripts) {
    try {
      const p = new URL(s.src, window.location.origin).pathname;
      if (p.startsWith("/assets/")) return p;
    } catch {
      /* malformed src — ignore */
    }
  }
  return null;
}

/** The hashed entry bundle currently DEPLOYED (fresh, never-cached index.html). */
async function deployedEntryPath(): Promise<string | null> {
  try {
    const res = await fetch(`/?_dv=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return null;
    const html = await res.text();
    const m = html.match(/<script[^>]+type="module"[^>]+src="(\/assets\/[^"]+\.js)"/);
    return m ? m[1] : null;
  } catch {
    // offline / fetch blocked → treat as "no change", try again on next focus
    return null;
  }
}

/**
 * Call `onNewVersion` once when a NEWER build than the one this tab is running
 * has been deployed. Checks only when the tab (re)gains focus — no background
 * polling. No-ops in dev (entry is /src/index.tsx, not /assets/…) and on fetch
 * failure. Returns a cleanup function (remove the listener).
 */
export function watchForNewDeployOnFocus(onNewVersion: () => void): () => void {
  const running = runningEntryPath();
  if (!running) return () => {}; // dev / unrecognized shape → nothing to compare
  let notified = false;
  const check = async () => {
    if (notified || document.visibilityState !== "visible") return;
    const deployed = await deployedEntryPath();
    if (deployed && deployed !== running) {
      notified = true;
      onNewVersion();
    }
  };
  document.addEventListener("visibilitychange", check);
  return () => document.removeEventListener("visibilitychange", check);
}
